import { ReactNode, useRef, useState } from 'react';

import { motion } from 'framer-motion';

import { Icon } from '@/new/components/icon';
import { DocumentTabs } from '@/new/components/reference-content/document-tabs';
import { cn } from '@/new/lib/utils';

interface ReferenceInfoPanelProps {
  /** Tabs shown above the panel (one per source document). */
  tabs: { id: string; label: string }[];
  selectedTabId: string | null;
  onSelectTab: (id: string) => void;
  /** Whether the whole panel is collapsed to a thin strip (e.g. after a viewer click). */
  isCollapsed: boolean;
  /** Called when the user mouses over the panel — used to un-collapse it. */
  onUncollapse: () => void;
  /**
   * Always-visible content (header, summary, sources, etc.). May be a render function
   * that receives the current expanded state (e.g. to clamp a summary when collapsed).
   */
  children: ReactNode | ((isExpanded: boolean) => ReactNode);
  /** Optional content revealed by the expand toggle. */
  expandableContent?: ReactNode;
  /**
   * Whether to show the expand toggle. Defaults to whether `expandableContent` is set —
   * pass explicitly when the always-visible content itself changes on expand (e.g. a
   * summary that un-clamps).
   */
  showToggle?: boolean;
}

/**
 * Shared bottom info panel for the reference side panel, used by both fact and document
 * drawers. Renders the document tabs, a collapsible panel, and an optional expandable
 * section toggled by a centered "..." / chevron button.
 */
export function ReferenceInfoPanel({
  tabs,
  selectedTabId,
  onSelectTab,
  isCollapsed,
  onUncollapse,
  children,
  expandableContent,
  showToggle,
}: ReferenceInfoPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const toggleVisible = showToggle ?? !!expandableContent;

  return (
    <div className="z-10 max-h-9/10 overflow-hidden" onMouseEnter={onUncollapse}>
      <DocumentTabs tabs={tabs} selectedId={selectedTabId} onSelect={onSelectTab} />

      <motion.div
        className="relative z-20 -mt-px overflow-hidden border-t bg-white text-xs shadow-[0_0_28px_rgba(0,0,0,0.2)]"
        animate={{ height: isCollapsed ? '16px' : 'auto' }}
        transition={{ duration: 0.2 }}
      >
        <div
          ref={scrollContainerRef}
          className={cn(isExpanded ? 'overflow-y-auto' : 'overflow-hidden', 'max-h-[calc(90vh-10rem)] px-5')}
        >
          {typeof children === 'function' ? children(isExpanded) : children}

          {toggleVisible && (
            <>
              <div className="pt-1.5 pb-2">
                <button
                  className="flex w-full justify-center p-1 text-gray-400 duration-150 hover:text-gray-900"
                  onClick={() => {
                    if (isExpanded) {
                      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                    setIsExpanded(!isExpanded);
                  }}
                >
                  <Icon
                    className={cn(
                      isExpanded ? 'lucide--chevron-down' : 'lucide--more-horizontal',
                      'text-base duration-300',
                    )}
                  />
                </button>
              </div>

              {expandableContent && (
                <motion.div
                  animate={{ height: isExpanded ? 'auto' : 0 }}
                  transition={{ duration: 0.2 }}
                  initial={false}
                  className="overflow-hidden"
                >
                  {expandableContent}
                </motion.div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
